import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router'
import { StreamConfigView } from '@/components/stream/StreamConfigView'
import { accountQuery, coverageListsQuery, subaccountsQuery, topicsQuery } from '@/lib/queries'
import { streamConfigQuery } from '@/lib/stream-queries'

/**
 * A client workspace's stream configuration — the client's own topics only (a sibling's coverage
 * comes in only by rolling that sub-account up, under its own topics), with the account's coverage
 * lists available as live references, exactly as on a report. Redirects to the CLIENT Home when
 * unentitled, like the pages it configures.
 */
export const Route = createFileRoute(
  '/app/accounts/$accountId_/subaccounts/$subaccountId/stream-config',
)({
  loader: async ({ context, params }) => {
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    const entitled =
      account.entitlements.includes(PRODUCT_ENTITLEMENTS.library) ||
      account.entitlements.includes(PRODUCT_ENTITLEMENTS.analytics)
    if (!entitled) {
      throw redirect({
        to: '/app/accounts/$accountId/subaccounts/$subaccountId',
        params: { accountId: params.accountId, subaccountId: params.subaccountId },
      })
    }
    await Promise.all([
      context.queryClient.ensureQueryData(subaccountsQuery(params.accountId)),
      context.queryClient.ensureQueryData(coverageListsQuery(params.accountId)),
      context.queryClient.ensureQueryData(topicsQuery(params.subaccountId)),
      context.queryClient.ensureQueryData(streamConfigQuery(params.subaccountId)),
    ])
  },
  component: ClientStreamConfigPage,
})

function ClientStreamConfigPage() {
  const { accountId, subaccountId } = Route.useParams()
  const navigate = useNavigate()
  const router = useRouter()
  const { data: subsData } = useSuspenseQuery(subaccountsQuery(accountId))
  const { data: listsData } = useSuspenseQuery(coverageListsQuery(accountId))
  const { data: topicsData } = useSuspenseQuery(topicsQuery(subaccountId))
  const client = subsData.subaccounts.find((sub) => sub.id === subaccountId)

  return (
    <StreamConfigView
      workspaceName={client?.name ?? 'This client'}
      subaccountId={subaccountId}
      accountId={accountId}
      groups={[
        {
          subaccountId,
          owner: client?.name ?? 'This client',
          isPrimary: client?.isPrimary ?? false,
          topics: topicsData.topics,
        },
      ]}
      lists={listsData.lists}
      subaccounts={subsData.subaccounts}
      onBack={() => {
        if (router.history.canGoBack()) router.history.back()
        else
          void navigate({
            to: '/app/accounts/$accountId/subaccounts/$subaccountId/library',
            params: { accountId, subaccountId },
          })
      }}
    />
  )
}
